"use client";

// StepShell — the stepped window, seated in THE sheet (law_sheet_rises,
// 2026-10-08). It used to be a card of its own (its own scrim, scroll lock,
// wheel guard, veiled-room mode); now it is EtherModal with the steps' grammar
// laid in, so the whole connections model rises from the bottom edge, holds ONE
// height for its whole life, and shares one padding with every other sheet.
// ImportSteps (bring memory in) is built on it (the old connect and mint windows
// were too, before ConnectGuideModal), so it reads as one design system.
//
//   head      the sheet's title row (a mark and a name), seated as .eth-title
//   body      the step's title, its one line (`why`), then the matter
//   foot      progress dots at the left (click any to skip around), then
//             back · skip as quiet words and the step's ONE action, bottom
//             right. A step with no forward act of its own (its choice is made
//             in the body) offers `primary.quiet`: its label is a quiet link.
//   tabs      a window that walks distinct subjects (name · connect · …) names
//             them in the head as the navigator's own words; the dots give way
//             to them and ← → / a swipe step the subjects
//
// Closing is the × and Esc. A stray press on the veil does NOT drop a half-done
// import (the sheet's veil would close it); that one press is held here.
//
// `open` lets a caller that mounts this conditionally play the way down first
// (lib/os/useSheetLeave).

import { useId } from "react";
import EtherModal from "../ether/EtherModal";
import { PillButton } from "../ui";

export interface FlowStep {
  key: string;
  title: string;
  /** one short line under the title: what this step is for. */
  why?: React.ReactNode;
  body: React.ReactNode;
  /** the single primary action for this step (advance, or perform + advance). A
   *  `quiet` one is a step with no forward act of its own: a quiet link, not a pill. */
  primary: { label: string; onClick: () => void; disabled?: boolean; loading?: boolean; done?: boolean; quiet?: boolean };
  /** a step that can be passed over offers it here, beside back — never as a
   *  second button in the body. */
  skip?: { label: string; onClick: () => void };
  /** omit this step from the progress dots (e.g. a terminal "done" panel). */
  hideDot?: boolean;
}

export interface ShellTab {
  key: string;
  /** one or two words, lowercase. */
  label: string;
  /** this subject is settled (the name is minted, the tool has called). The
   *  navigator draws no mark for it; kept so a caller's tabs need not change. */
  done?: boolean;
}

/** The veiled guide window is gone (the sheet's own veil is the room's one
 *  frost, and netti rests behind it). OsShell still listens for these, so they
 *  stay exported; nothing dispatches the event any more. */
export const GUIDE_VEIL_NETTI_Z = 1195;
export const GUIDE_VEIL_EVENT = "innernet:guide-veil";

const Check = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5 10 17.5 19 7" />
  </svg>
);

const norm = (s: string) => s.trim().toLowerCase();

export default function StepShell({
  head,
  steps,
  step,
  setStep,
  onClose,
  onExit,
  ariaLabel,
  tabs,
  foot,
  open = true,
}: {
  head: React.ReactNode;
  steps: FlowStep[];
  step: number;
  setStep: (n: number) => void;
  onClose: () => void;
  /** back action when already on the first step (e.g. return to a prior phase). */
  onExit?: () => void;
  ariaLabel: string;
  /** named subjects in the head instead of dots; one per step, in order. */
  tabs?: ShellTab[];
  /** a quiet slot at the left of the foot (letting a connection go). */
  foot?: React.ReactNode;
  /** false plays the way down; the caller unmounts afterwards. */
  open?: boolean;
}) {
  const titleId = useId();
  const clamped = Math.max(0, Math.min(step, steps.length - 1));
  const s = steps[clamped];
  const dotSteps = steps.map((st, i) => ({ st, i })).filter(({ st }) => !st.hideDot);
  const canBack = clamped > 0 || !!onExit;
  const goBack = () => (clamped > 0 ? setStep(clamped - 1) : onExit?.());
  // the navigator already says which subject this is; the body does not say it again
  const sayTitle = !(tabs && tabs[clamped] && norm(tabs[clamped].label) === norm(s.title));

  const nav = {
    crumb: [],
    // the body arrives from the side the step lies on. A flow does not wrap
    // (total 0), so a jump from the first dot to the last still comes from the right.
    stepKey: s.key,
    position: { index: clamped, total: 0 },
    ...(tabs && tabs.length > 1
      ? {
          sections: tabs.map((t) => ({ id: t.key, label: t.label })),
          section: tabs[clamped]?.key,
          onSection: (id: string) => {
            const i = tabs.findIndex((t) => t.key === id);
            if (i >= 0) setStep(i);
          },
        }
      : null),
  };

  const primary = s.primary.quiet ? (
    <button type="button" className="quiet-link" onClick={s.primary.onClick} disabled={s.primary.disabled || s.primary.loading}>
      {s.primary.label}
    </button>
  ) : (
    <PillButton
      variant="primary"
      icon={s.primary.done ? <Check /> : undefined}
      loading={s.primary.loading}
      disabled={s.primary.disabled}
      onClick={s.primary.onClick}
    >
      {s.primary.label}
    </PillButton>
  );

  const dots =
    !tabs && dotSteps.length > 1 ? (
      <div className="cm-dots" role="group" aria-label="steps">
        {dotSteps.map(({ st, i }) => (
          <button key={st.key} type="button" className="cm-dot" data-on={i === clamped || undefined} aria-current={i === clamped ? "step" : undefined} onClick={() => setStep(i)}>
            <span className="eth-vh">{`step ${i + 1}`}</span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    // the veil's one press is held here, before the sheet hears it (see the header)
    <div
      style={{ display: "contents" }}
      onClickCapture={(e) => {
        if (e.target instanceof Element && e.target.classList.contains("eth-veil")) e.stopPropagation();
      }}
    >
      <EtherModal
        open={open}
        onClose={onClose}
        labelledBy={titleId}
        size="md"
        height="tall"
        className="cm-sheet"
        header={
          <h2 id={titleId} className="eth-title cm-headrow" aria-label={ariaLabel}>
            {head}
          </h2>
        }
        nav={nav}
        footStart={tabs ? foot : dots}
        footer={
          <div className="cm-acts">
            {canBack ? (
              <button type="button" className="quiet-link" onClick={goBack}>
                back
              </button>
            ) : null}
            {s.skip ? (
              <button type="button" className="quiet-link" onClick={s.skip.onClick}>
                {s.skip.label}
              </button>
            ) : null}
            {primary}
          </div>
        }
      >
        <div className="cm-body">
          {sayTitle ? <div className="cm-steptitle">{s.title}</div> : null}
          {s.why ? <p className="cm-why">{s.why}</p> : null}
          {s.body}
        </div>
      </EtherModal>
    </div>
  );
}
